import { useState, type FormEvent } from "react";
import { useParams, Link } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Edit,
  Phone,
  Mail,
  FileText,
  Scale,
  Upload,
  Database,
  ClipboardList,
  FolderOpen,
  LogOut,
  UserPlus,
  UserMinus,
  BarChart3,
  Target,
  ShieldAlert,
  Users,
  Gavel,
  Eye,
  Puzzle,
  DollarSign,
  ArrowRightLeft,
  Heart,
  ClipboardCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Select, type SelectOption } from "@/components/ui/Select";
import { cn } from "@/lib/cn";
import { CaseNotesList } from "@/features/case-notes/CaseNotesList";
import { ActivitiesList } from "@/features/activities/ActivitiesList";
import { CasesList } from "@/features/case-management/CasesList";
import { DepartureForm } from "@/features/departure/DepartureForm";
import { DepartureView } from "@/features/departure/DepartureView";
import { DataTrackingList } from "@/features/data-tracking/DataTrackingList";
import { OutcomesList } from "@/features/outcomes/OutcomesList";
import { LegalCasesList } from "@/features/legal/LegalCasesList";
import { ProtectionOrdersList } from "@/features/legal/ProtectionOrdersList";
import { CourtAdvocacyList } from "@/features/legal/CourtAdvocacyList";
import { AbusersList } from "@/features/abusers/AbusersList";
import { PerpetratorsList } from "@/features/perpetrators/PerpetratorsList";
import { BattererSessions } from "@/features/batterer/BattererSessions";
import { VisitationCases } from "@/features/visitation/VisitationCases";
import { PlayTherapySessions } from "@/features/play-therapy/PlayTherapySessions";
import { HmisList } from "@/features/hmis/HmisList";
import { HmisForm } from "@/features/hmis/HmisForm";
import { SurveyRunner } from "@/features/surveys/SurveyRunner";
import { AssessmentList } from "@/features/surveys/AssessmentList";
import { FinancialAssistance } from "@/features/financial/FinancialAssistance";
import { PaymentsList } from "@/features/financial/PaymentsList";
import { ReferralsList } from "@/features/referrals/ReferralsList";
import { MedicalAdvocacyList } from "@/features/medical/MedicalAdvocacyList";

interface ClientDetail {
  id: string;
  firstName: string;
  lastName: string;
  clientType: string;
  status: "active" | "inactive" | "exited" | "waitlist";
  intakeDate: string;
  dateOfBirth: string;
  gender: string;
  race: string;
  ethnicity: string;
  phone: string;
  email: string;
  address: string;
  advocate: string;
  caseNotes: CaseNote[];
  activities: Activity[];
}

interface CaseNote {
  id: string;
  date: string;
  author: string;
  content: string;
  type: string;
}

interface Activity {
  id: string;
  date: string;
  type: string;
  description: string;
  duration?: string;
}

const placeholderClient: ClientDetail = {
  id: "CL-1001",
  firstName: "Maria",
  lastName: "Gonzalez",
  clientType: "adult-shelter",
  status: "active",
  intakeDate: "2026-01-15",
  dateOfBirth: "1988-06-22",
  gender: "Female",
  race: "Hispanic/Latina",
  ethnicity: "Mexican",
  phone: "(555) 234-5678",
  email: "",
  address: "Confidential - Shelter Resident",
  advocate: "Sarah M.",
  caseNotes: [
    {
      id: "cn-1",
      date: "2026-03-14",
      author: "Sarah M.",
      content:
        "Met with client to discuss safety planning and housing options. Client expressed interest in transitional housing program. Provided referral to housing navigator.",
      type: "Case Management",
    },
    {
      id: "cn-2",
      date: "2026-03-10",
      author: "Sarah M.",
      content:
        "Client attended group counseling session. Participated actively in discussion about healthy relationships.",
      type: "Counseling",
    },
    {
      id: "cn-3",
      date: "2026-03-05",
      author: "David R.",
      content:
        "Assisted client with protection order paperwork. Court hearing scheduled for March 20.",
      type: "Legal",
    },
  ],
  activities: [
    {
      id: "a-1",
      date: "2026-03-14",
      type: "Case Management",
      description: "Individual session - safety planning",
      duration: "45 min",
    },
    {
      id: "a-2",
      date: "2026-03-12",
      type: "Group",
      description: "Support group session",
      duration: "90 min",
    },
    {
      id: "a-3",
      date: "2026-03-10",
      type: "Counseling",
      description: "Group counseling - healthy relationships",
      duration: "60 min",
    },
    {
      id: "a-4",
      date: "2026-03-05",
      type: "Legal",
      description: "Protection order assistance",
      duration: "30 min",
    },
  ],
};

const detailTabs = [
  { id: "demographics", label: "Demographics", icon: ClipboardList },
  { id: "notes", label: "Case Notes", icon: FileText },
  { id: "activities", label: "Activities", icon: ClipboardList },
  { id: "cases", label: "Case Management", icon: FolderOpen },
  { id: "tracking", label: "Data Tracking", icon: BarChart3 },
  { id: "outcomes", label: "Outcomes", icon: Target },
  { id: "abusers", label: "Abusers", icon: ShieldAlert },
  { id: "perpetrators", label: "Perpetrators", icon: Users },
  { id: "departure", label: "Departure", icon: LogOut },
  { id: "legal", label: "Legal", icon: Scale },
  { id: "batterer", label: "Batterer Program", icon: Gavel },
  { id: "visitation", label: "Visitation", icon: Eye },
  { id: "play-therapy", label: "Play Therapy", icon: Puzzle },
  { id: "surveys", label: "Surveys", icon: ClipboardCheck },
  { id: "financial", label: "Financial", icon: DollarSign },
  { id: "referrals", label: "Referrals", icon: ArrowRightLeft },
  { id: "medical", label: "Medical Advocacy", icon: Heart },
  { id: "documents", label: "Documents", icon: Upload },
  { id: "hmis", label: "HMIS", icon: Database },
] as const;

type TabId = (typeof detailTabs)[number]["id"];

const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  inactive: "bg-gray-100 text-gray-800",
  exited: "bg-amber-100 text-amber-800",
  waitlist: "bg-blue-100 text-blue-800",
};

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabId>("demographics");
  const [showDepartureForm, setShowDepartureForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [autoOpenNoteEditor, setAutoOpenNoteEditor] = useState(false);
  const queryClient = useQueryClient();

  // Check if client is in My Clients
  const { data: myClients } = useQuery<{ id: number }[]>({
    queryKey: ["myClients"],
    queryFn: async () => {
      try {
        const res = await api.get<{ data: { id: number }[] }>("/my-clients");
        return res.data;
      } catch {
        return [];
      }
    },
  });

  const isInMyClients = (myClients ?? []).some(
    (c) => String(c.id) === id,
  );

  const addToMyClientsMutation = useMutation({
    mutationFn: () => api.post(`/my-clients/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myClients"] });
    },
  });

  const removeFromMyClientsMutation = useMutation({
    mutationFn: () => api.delete(`/my-clients/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myClients"] });
    },
  });

  const { data: client, isLoading } = useQuery<ClientDetail>({
    queryKey: ["client", id],
    queryFn: async () => {
      try {
        const res = await api.get<{ data: Record<string, unknown> }>(`/clients/${id}`);
        const d = res.data;

        // Map API snake_case fields to frontend camelCase
        const personTypeMap: Record<string, string> = {
          shelter: "adult-shelter",
          child: "child-shelter",
          outreach: "outreach",
          hotline: "hotline",
          "adult-shelter": "adult-shelter",
          "child-shelter": "child-shelter",
        };
        const statusMap: Record<string, string> = {
          active: "active",
          inactive: "inactive",
          exited: "exited",
          departed: "exited",
          waitlist: "waitlist",
          incomplete: "inactive",
        };

        return {
          id: (d.internal_id as string) || (d.internalId as string) || String(d.id),
          firstName: (d.first_name as string) || (d.firstName as string) || "",
          lastName: (d.last_name as string) || (d.lastName as string) || "",
          clientType: personTypeMap[(d.person_type as string) || (d.clientType as string) || ""] || "outreach",
          status: (statusMap[(d.status as string) || "active"] || "active") as ClientDetail["status"],
          intakeDate: (d.intake_date as string) || (d.intakeDate as string) || "",
          dateOfBirth: (d.date_of_birth as string) || (d.dateOfBirth as string) || "",
          gender: (d.gender as string) || "",
          race: (d.race as string) || "",
          ethnicity: (d.ethnicity as string) || "",
          phone: (d.phone_mobile as string) || (d.phone_home as string) || (d.phone as string) || "",
          email: (d.email as string) || "",
          address: [d.address_street, d.address_city, d.address_state, d.address_zip]
            .filter(Boolean).join(", ") || (d.address as string) || "",
          advocate: (d.advocate as string) || "",
          caseNotes: (d.caseNotes as CaseNote[]) || [],
          activities: (d.activities as Activity[]) || [],
        };
      } catch {
        return { ...placeholderClient, id: id ?? "CL-0000" };
      }
    },
    enabled: !!id,
    placeholderData: { ...placeholderClient, id: id ?? "CL-0000" },
  });

  if (isLoading && !client) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/clients">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        </div>
        <div className="h-64 animate-pulse rounded-lg border bg-muted" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/clients">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-foreground">
            Client not found
          </h1>
        </div>
        <p className="text-sm text-muted-foreground">
          The client record could not be loaded.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/clients">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">
                {client.firstName} {client.lastName}
              </h1>
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                  statusColors[client.status] ?? "bg-gray-100 text-gray-800",
                )}
              >
                {client.status}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              {client.id} &middot; {client.clientType.replace("-", " ")} &middot; Advocate:{" "}
              {client.advocate}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {/* Add/Remove from My Clients */}
          {isInMyClients ? (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => removeFromMyClientsMutation.mutate()}
              loading={removeFromMyClientsMutation.isPending}
            >
              <UserMinus className="h-4 w-4" />
              Remove from My Clients
            </Button>
          ) : (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => addToMyClientsMutation.mutate()}
              loading={addToMyClientsMutation.isPending}
            >
              <UserPlus className="h-4 w-4" />
              Add to My Clients
            </Button>
          )}

          {/* Record Departure for active clients */}
          {client.status === "active" && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => {
                setActiveTab("departure");
                setShowDepartureForm(true);
              }}
            >
              <LogOut className="h-4 w-4" />
              Record Departure
            </Button>
          )}

          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              setActiveTab("demographics");
              setIsEditing(true);
            }}
          >
            <Edit className="h-4 w-4" />
            Edit
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              setAutoOpenNoteEditor(true);
              setActiveTab("notes");
            }}
          >
            <FileText className="h-4 w-4" />
            Add Note
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="rounded-xl border bg-muted/30 p-2">
        <nav className="flex flex-wrap gap-1.5">
          {detailTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all",
                activeTab === tab.id
                  ? "bg-background text-primary shadow-sm ring-1 ring-border"
                  : "text-muted-foreground hover:bg-background/60 hover:text-foreground",
              )}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === "demographics" && (
        <DemographicsTab
          client={client}
          clientId={id!}
          isEditing={isEditing}
          onCancelEdit={() => setIsEditing(false)}
          onSaved={() => {
            setIsEditing(false);
            queryClient.invalidateQueries({ queryKey: ["client", id] });
          }}
        />
      )}
      {activeTab === "notes" && (
        <CaseNotesList
          personId={id!}
          autoOpenEditor={autoOpenNoteEditor}
          onEditorOpened={() => setAutoOpenNoteEditor(false)}
        />
      )}
      {activeTab === "activities" && <ActivitiesList personId={id!} />}
      {activeTab === "cases" && <CasesList personId={id!} />}
      {activeTab === "tracking" && <DataTrackingList personId={id!} />}
      {activeTab === "outcomes" && <OutcomesList personId={id!} />}
      {activeTab === "abusers" && <AbusersList personId={id!} />}
      {activeTab === "perpetrators" && <PerpetratorsList personId={id!} />}
      {activeTab === "departure" && (
        client.status === "exited" ? (
          <DepartureView
            personId={id!}
            personName={`${client.firstName} ${client.lastName}`}
            onUndone={() => {
              queryClient.invalidateQueries({ queryKey: ["client", id] });
              setActiveTab("demographics");
            }}
          />
        ) : showDepartureForm ? (
          <DepartureForm
            personId={id!}
            personName={`${client.firstName} ${client.lastName}`}
            onComplete={() => {
              setShowDepartureForm(false);
              queryClient.invalidateQueries({ queryKey: ["client", id] });
            }}
          />
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="mb-4 text-muted-foreground">
                This client has not been exited yet.
              </p>
              <Button
                onClick={() => setShowDepartureForm(true)}
                className="gap-2"
              >
                <LogOut className="h-4 w-4" />
                Record Departure
              </Button>
            </CardContent>
          </Card>
        )
      )}
      {activeTab === "legal" && (
        <div className="space-y-8">
          <LegalCasesList personId={id!} />
          <ProtectionOrdersList personId={id!} />
          <CourtAdvocacyList personId={id!} />
        </div>
      )}
      {activeTab === "batterer" && <BattererSessions personId={id!} />}
      {activeTab === "visitation" && <VisitationCases personId={id!} />}
      {activeTab === "play-therapy" && <PlayTherapySessions personId={id!} />}
      {activeTab === "surveys" && (
        <div className="space-y-8">
          <SurveyRunner personId={id!} />
          <AssessmentList personId={id!} />
        </div>
      )}
      {activeTab === "financial" && (
        <div className="space-y-8">
          <FinancialAssistance personId={id!} />
          <PaymentsList personId={id!} />
        </div>
      )}
      {activeTab === "referrals" && <ReferralsList personId={id!} />}
      {activeTab === "medical" && <MedicalAdvocacyList personId={id!} />}
      {activeTab === "documents" && <PlaceholderTab name="Documents" />}
      {activeTab === "hmis" && (
        <div className="space-y-8">
          <HmisForm personId={id!} />
          <HmisList personId={id!} />
        </div>
      )}
    </div>
  );
}

const genderOptions: SelectOption[] = [
  { value: "", label: "Select..." },
  { value: "Female", label: "Female" },
  { value: "Male", label: "Male" },
  { value: "Non-Binary", label: "Non-Binary" },
  { value: "Transgender", label: "Transgender" },
  { value: "Other", label: "Other" },
  { value: "Unknown", label: "Unknown" },
];

const statusOptions: SelectOption[] = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "exited", label: "Exited" },
  { value: "waitlist", label: "Waitlist" },
];

function DemographicsTab({
  client,
  clientId,
  isEditing,
  onCancelEdit,
  onSaved,
}: {
  client: ClientDetail;
  clientId: string;
  isEditing: boolean;
  onCancelEdit: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    firstName: client.firstName,
    lastName: client.lastName,
    dateOfBirth: client.dateOfBirth,
    gender: client.gender,
    race: client.race,
    ethnicity: client.ethnicity,
    phone: client.phone,
    email: client.email,
    address: client.address,
    status: client.status,
    advocate: client.advocate,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Reset form when client data changes or editing starts
  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.put(`/clients/${clientId}`, form);
      onSaved();
    } catch (err) {
      setError((err as Error)?.message ?? "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  if (!isEditing) {
    return (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Personal Information</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3 text-sm">
              <DetailRow label="Full Name" value={`${client.firstName} ${client.lastName}`} />
              <DetailRow label="Date of Birth" value={client.dateOfBirth ? new Date(client.dateOfBirth).toLocaleDateString() : "—"} />
              <DetailRow label="Gender" value={client.gender || "—"} />
              <DetailRow label="Race" value={client.race || "—"} />
              <DetailRow label="Ethnicity" value={client.ethnicity || "—"} />
              <DetailRow label="Intake Date" value={client.intakeDate ? new Date(client.intakeDate).toLocaleDateString() : "—"} />
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contact Information</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3 text-sm">
              {client.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <DetailRow label="Phone" value={client.phone} />
                </div>
              )}
              {client.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <DetailRow label="Email" value={client.email} />
                </div>
              )}
              <DetailRow label="Address" value={client.address || "—"} />
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Case Information</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3 text-sm">
              <DetailRow label="Client ID" value={client.id} />
              <DetailRow label="Program" value={client.clientType.replace("-", " ")} />
              <DetailRow label="Status" value={client.status} />
              <DetailRow label="Assigned Advocate" value={client.advocate || "—"} />
            </dl>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Edit mode
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Personal Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First Name"
                value={form.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
                required
              />
              <Input
                label="Last Name"
                value={form.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
                required
              />
            </div>
            <Input
              label="Date of Birth"
              type="date"
              value={form.dateOfBirth}
              onChange={(e) => handleChange("dateOfBirth", e.target.value)}
            />
            <Select
              label="Gender"
              options={genderOptions}
              value={form.gender}
              onChange={(v) => handleChange("gender", v)}
            />
            <Input
              label="Race"
              value={form.race}
              onChange={(e) => handleChange("race", e.target.value)}
            />
            <Input
              label="Ethnicity"
              value={form.ethnicity}
              onChange={(e) => handleChange("ethnicity", e.target.value)}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Phone"
              value={form.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
            />
            <Input
              label="Address"
              value={form.address}
              onChange={(e) => handleChange("address", e.target.value)}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Case Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input label="Client ID" value={client.id} disabled />
            <Select
              label="Status"
              options={statusOptions}
              value={form.status}
              onChange={(v) => handleChange("status", v)}
            />
            <Input
              label="Assigned Advocate"
              value={form.advocate}
              onChange={(e) => handleChange("advocate", e.target.value)}
            />
          </CardContent>
        </Card>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <Button type="submit" className="gap-2" loading={saving}>
          <Edit className="h-4 w-4" />
          Save Changes
        </Button>
        <Button type="button" variant="outline" onClick={onCancelEdit}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function PlaceholderTab({ name }: { name: string }) {
  return (
    <Card>
      <CardContent className="p-8 text-center">
        <p className="text-muted-foreground">
          {name} tab is under construction.
        </p>
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium capitalize">{value}</dd>
    </div>
  );
}
